/**
 * Emoji reaction bar — inline, session-based (one-per-emoji-per-session).
 *
 * Props:
 *   - target_type: "article" | "live_update" | "film_play"
 *   - target_id:   the article id, update id, etc.
 *   - size:        "sm" (article title strip, live updates) | "md"
 *
 * Behavior:
 *   Loads counts on mount. Clicking toggles the current session's vote for that
 *   emoji. The backend returns fresh counts + this session's picks after each toggle.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getSessionId } from "@/lib/analytics";

// Kept in sync with backend ALLOWED_EMOJIS.
export const REACTIONS = [
  { key: "fire",  emoji: "🔥", label: "Fire" },
  { key: "money", emoji: "💯", label: "Money" },
  { key: "flex",  emoji: "💪", label: "Titan Up" },
  { key: "shook", emoji: "😱", label: "Shook" },
  { key: "lol",   emoji: "😂", label: "LOL" },
  { key: "eyes",  emoji: "👀", label: "Watching" },
];

export default function ReactionBar({ target_type, target_id, size = "sm" }) {
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(null); // emoji key currently toggling
  const [poppingKey, setPoppingKey] = useState(null);

  const sessionId = typeof window !== "undefined" ? (getSessionId() || "") : "";

  const load = useCallback(async () => {
    if (!target_id) return;
    try {
      const { data } = await api.get(`/reactions/${target_type}/${target_id}`, {
        params: { session_id: sessionId },
      });
      setCounts(data?.counts || {});
      setMine(data?.mine || []);
    } catch {
      /* silent — reactions are non-critical */
    }
  }, [target_type, target_id, sessionId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    if (busy) return;
    setBusy(key);
    setPoppingKey(key);
    try {
      const { data } = await api.post("/reactions/toggle", {
        target_type,
        target_id,
        emoji: key,
        session_id: sessionId,
      });
      setCounts(data?.counts || {});
      setMine(data?.mine || []);
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
      setTimeout(() => setPoppingKey(null), 550);
    }
  };

  const isSmall = size !== "md";
  const btnCls = isSmall
    ? "px-2 py-1 text-xs border rounded-full inline-flex items-center gap-1 transition-all"
    : "px-3 py-1.5 text-sm border rounded-full inline-flex items-center gap-1.5 transition-all";

  return (
    <div
      className={`flex flex-wrap ${isSmall ? "gap-1.5" : "gap-2"}`}
      data-testid={`reactions-${target_type}-${target_id}`}
    >
      {REACTIONS.map((r) => {
        const n = counts[r.key] || 0;
        const active = mine.includes(r.key);
        const popping = poppingKey === r.key;
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => toggle(r.key)}
            disabled={busy && busy !== r.key}
            aria-pressed={active}
            aria-label={r.label}
            title={r.label}
            data-testid={`reaction-${target_type}-${target_id}-${r.key}`}
            className={`relative ${btnCls} ${
              active
                ? "bg-titans-red/15 border-titans-red text-white"
                : "border-[rgba(138,141,143,0.3)] text-titans-silver hover:border-titans-blue hover:text-titans-blue"
            } disabled:opacity-60`}
          >
            <span className={`${isSmall ? "text-sm" : "text-base"} ${popping ? "emoji-pop" : ""}`}>{r.emoji}</span>
            {n > 0 && (
              <span key={n} className="count-tick font-cond uppercase tracking-wider text-[10px]">{n}</span>
            )}
            {popping && (
              <span className="absolute inset-0 pointer-events-none" aria-hidden="true">
                {[...Array(6)].map((_, j) => (
                  <span
                    key={j}
                    className="burst-p"
                    style={{
                      background: j % 2 ? "#C8102E" : "#4B92DB",
                      "--bx": `${Math.round(Math.cos((j / 6) * Math.PI * 2) * 22)}px`,
                      "--by": `${Math.round(Math.sin((j / 6) * Math.PI * 2) * 22)}px`,
                    }}
                  />
                ))}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
